// src/lib/api/jobApplicationApi.ts

import { getApiClient } from './client';
import { ApplicantProfile } from '../models/applicantProfile';
import { CandidateEvaluation, FeatureFlags, MentionApplication } from '../models/candidateEvaluation';
import {
    JobApplication,
    JobApplicationListResponse,
    CreateJobApplicationRequest,
    UpdateJobApplicationStatusRequest,
    PublicNeed,
    PublicNeedListResponse,
    PublicNeedQuery,
    NeedFacetsResponse,
} from '../models/jobApplication';

// ===================================
// PUBLIC JOB BOARD (no auth required)
// ===================================

// Server-side structured search (#39). Competencies go out as repeated params
// (competencies=a&competencies=b) — built by hand so we don't rely on the HTTP
// client's array serialization, which gin's form binding wouldn't accept.
const listPublicNeeds = async (query: PublicNeedQuery = {}): Promise<PublicNeedListResponse> => {
    try {
        const sp = new URLSearchParams();
        if (query.page) sp.set('page', String(query.page));
        if (query.limit) sp.set('limit', String(query.limit));
        if (query.q) sp.set('q', query.q);
        if (query.city) sp.set('city', query.city);
        if (query.employment_type) sp.set('employment_type', query.employment_type);
        if (query.employment_extent) sp.set('employment_extent', query.employment_extent);
        if (query.work_location) sp.set('work_location', query.work_location);
        if (query.supervision) sp.set('supervision', 'true');
        if (query.sort) sp.set('sort', query.sort);
        (query.competencies ?? []).forEach((c) => sp.append('competencies', c));
        const qs = sp.toString();
        const data = await getApiClient().get<PublicNeedListResponse>(
            `/api/public/needs${qs ? `?${qs}` : ''}`,
        );
        return data;
    } catch (error) {
        console.error('[jobApplicationApi] Error fetching public needs:', error);
        return { needs: [], total: 0 };
    }
};

// Distinct cities/competencies across all active needs, for the filter dropdowns.
const getNeedFacets = async (): Promise<NeedFacetsResponse> => {
    try {
        return await getApiClient().get<NeedFacetsResponse>('/api/public/need-facets');
    } catch (error) {
        console.error('[jobApplicationApi] Error fetching need facets:', error);
        return { cities: [], competencies: [] };
    }
};

const getPublicNeedById = async (id: string): Promise<PublicNeed> => {
    return await getApiClient().get<PublicNeed>(`/api/public/needs/${id}`);
};

// Signed-in view of a single posting: active postings, OR one the caller has
// applied to (so applied postings stay viewable after the employer
// unpublishes/closes them — #276). Same response shape as getPublicNeedById.
const getNeedForViewer = async (id: string): Promise<PublicNeed> => {
    return await getApiClient().get<PublicNeed>(`/api/needs/${id}/view`);
};

// ===================================
// CANDIDATE APPLICATION ACTIONS
// ===================================

const applyToNeed = async (needId: string, data: CreateJobApplicationRequest): Promise<JobApplication> => {
    return await getApiClient().post<JobApplication>(`/api/needs/${needId}/apply`, data);
};

const getMyApplications = async (page: number = 1, limit: number = 20): Promise<JobApplicationListResponse> => {
    try {
        const data = await getApiClient().get<JobApplicationListResponse>('/api/my/applications', {
            params: { page, limit }
        });
        return data;
    } catch (error) {
        console.error('[jobApplicationApi] Error fetching my applications:', error);
        return { applications: [], total: 0 };
    }
};

const withdrawApplication = async (applicationId: string): Promise<JobApplication> => {
    return await getApiClient().post<JobApplication>(`/api/applications/${applicationId}/withdraw`, {});
};

// ===================================
// EMPLOYER APPLICATION REVIEW
// ===================================

const getApplicationsForNeed = async (needId: string, page: number = 1, limit: number = 20): Promise<JobApplicationListResponse> => {
    try {
        const data = await getApiClient().get<JobApplicationListResponse>(`/api/needs/${needId}/applications`, {
            params: { page, limit }
        });
        return data;
    } catch (error) {
        console.error('[jobApplicationApi] Error fetching applications for need:', error);
        return { applications: [], total: 0 };
    }
};

const updateApplicationStatus = async (
    applicationId: string,
    data: UpdateJobApplicationStatusRequest
): Promise<JobApplication> => {
    return await getApiClient().patch<JobApplication>(`/api/applications/${applicationId}/status`, data);
};

// getApplicantProfile fetches the employer-facing view of an applicant (identity
// + candidate profile + documents). Backend gates it to the owner of the need.
const getApplicantProfile = async (applicationId: string): Promise<ApplicantProfile> => {
    return await getApiClient().get<ApplicantProfile>(`/api/applications/${applicationId}/applicant`);
};

// ===================================
// #47b — AI candidate evaluation (dark-launched)
// ===================================

// getFeatures returns runtime feature flags so the UI can hide dark-launched
// surfaces. Public + best-effort — defaults everything off on failure.
const getFeatures = async (): Promise<FeatureFlags> => {
    try {
        return await getApiClient().get<FeatureFlags>(`/api/public/features`);
    } catch {
        // Everything off / uncapped on failure: a features fetch that fails must
        // not lock the user out of starting a chat. The backend still enforces
        // the cap, so the worst case is the refusal arrives a step later.
        return { cv_evaluation: false, link_max_chats: 0 };
    }
};

// getEvaluation returns the cached AI evaluation for an application, or null if
// none has been run yet (backend returns { evaluation: null }).
const getEvaluation = async (applicationId: string): Promise<CandidateEvaluation | null> => {
    const res = await getApiClient().get<{ evaluation: CandidateEvaluation | null }>(
        `/api/applications/${applicationId}/evaluation`,
    );
    return res?.evaluation ?? null;
};

// evaluateApplicant runs (or re-runs) the AI evaluation and returns the result.
const evaluateApplicant = async (applicationId: string): Promise<CandidateEvaluation> => {
    return await getApiClient().post<CandidateEvaluation>(`/api/applications/${applicationId}/evaluate`, {});
};

// getMyMentionableApplications lists the employer's applications (minus
// withdrawn) for the Link @-mention picker. Best-effort — returns [] on failure
// (incl. a 404 when the CV feature is off), so the picker just omits
// applications rather than breaking.
const getMyMentionableApplications = async (): Promise<MentionApplication[]> => {
    try {
        const res = await getApiClient().get<{ applications: MentionApplication[] }>(
            `/api/applications/mine/mentionable`,
        );
        return res?.applications ?? [];
    } catch {
        return [];
    }
};

export const jobApplicationApi = {
    // Public job board
    listPublicNeeds,
    getNeedFacets,
    getPublicNeedById,
    getNeedForViewer,

    // Candidate actions
    applyToNeed,
    getMyApplications,
    withdrawApplication,

    // Employer actions
    getApplicationsForNeed,
    updateApplicationStatus,
    getApplicantProfile,

    // #47b — AI candidate evaluation (dark-launched)
    getFeatures,
    getEvaluation,
    evaluateApplicant,
    getMyMentionableApplications,
};
